/**
 * `@managing-up/engine` — the pure reducer core.
 *
 * Hard constraints (enforced by the `engine-boundary` ESLint rule, the
 * `no-restricted-properties` rules in `eslint.config.js`, and
 * `.dependency-cruiser.cjs`):
 *
 *   - no runtime dependencies other than `@managing-up/schema`
 *   - no I/O, no DOM, no Node builtins, no `Math.random`, no `Date`
 *   - state is plain serialisable JSON: no classes, `Map`, `Set`, or `Date`
 *
 * The same code runs in three hostile places — the browser shell, the solver's
 * inner loop, and the Node playtest harness — which is why it depends on
 * nothing and reads no clock.
 *
 * ## Using it
 *
 * ```ts
 * const game = openGame(pack, profile, 'seed-1');
 * const { state, effects, trace } = game.reduce(game.state, { type: 'SEND' });
 * ```
 *
 * `openGame` returns a handle that closes over the indexed package, so `reduce`
 * and `availableActions` take the state alone. The free functions of the same
 * name take the package explicitly and are what the solver calls in its hot
 * loop.
 */
import { SCHEMA_VERSION } from '@managing-up/schema';
import { availableActions as availableActionsWith } from './actions.js';
import { createGame } from './create.js';
import { reduce as reduceWith } from './reduce.js';
/** The schema version this engine build is compiled against. */
export const ENGINE_SCHEMA_VERSION = SCHEMA_VERSION;
/** Bumped when a save written by this engine can no longer be replayed by an older one. */
export const ENGINE_VERSION = '0.1.0';
/**
 * Opens a run: loads and indexes the package, resolves the difficulty profile
 * into settings, and builds the opening state.
 */
export function openGame(pack, profile, seed, options = {}) {
    const { loaded, state, effects } = createGame(pack, profile, seed, options);
    return {
        loaded,
        state,
        openingEffects: effects,
        reduce: (from, action) => reduceWith(loaded, from, action),
        availableActions: (from) => availableActionsWith(loaded, from),
    };
}
export { createGame, resolveSettings, starterParticles, DEFAULT_BANDWIDTH_CAPACITY, } from './create.js';
export { optionsFrom, profileIds, selectProfile, switchProfile, UnknownProfileError, } from './difficulty.js';
export { deriveHint, failingAxisOf, nearestMissedRule, nearestMissIn } from './hints.js';
export { reduce, canExecuteSyncPitch, canStageDraft, hintAvailable, targetable, undoAvailable, } from './reduce.js';
export { availableActions } from './actions.js';
export { applyPuppetGrants, bandwidthCostOf, freeBandwidth, puppetSelectable, releasePossession, swapGrants, takePossession, touchPuppet, } from './possession.js';
export { applyPerkEffects, equippedPerks, packWithPerks, perkParticleIds, phaseBudgetDeltaOf, PERK_PARTICLE_SOURCE, } from './perks.js';
export { cxpDelta, quarterlyReview, speedMultiplierFor } from './score.js';
export { loadPack, harvestKey, PackLoadError } from './pack.js';
export { nodeVarsOf, worldContext } from './context.js';
export { applyAttentionDecay, burialDepth, buriedOutcomeId, decayAmount, emitChatter, emitNoise, enterPhase, evaluateObjectives, runScheduledEvents, } from './world.js';
export { dmPartnerOf, harvestableIn, isDm, isReadable, messagesIn, threadRepliesOf, unreadTotal, visibleMessages, } from './channels.js';
export { canonicalState, semanticHash } from './hash.js';
export { aggregateTagSets, addTag, canonicalAggregate, clearAxis, countOf, distinctOn, emptyAggregate, isOrdinal, maxOrdinal, minOrdinal, removeTag, tagSetEntries, valuesOn, } from './tags.js';
export { collectFailures, compare, evaluate } from './predicates.js';
export { GATE_OUTCOME_IDS, runGates } from './gates.js';
export { applyOutcome, deliverDuePosts, pipLadderOf, pipRungAt, postMessage, withSuspicion, COMPOSER_LOCK_TICKS, DEFAULT_PIP_LADDER, OPENER_RESTRICTION_STRIKES, } from './outcomes.js';
export { evalContextFor, interceptableHops, proposalAfterLanding, proposalAfterSend, resolveAt, } from './resolve.js';
export { activeSlotDefs, admitsParticle, canSelect, chipsVisible, composerForIdentity, optionsForSlot, pickerOptionsForSlot, previewText, requiredSlotsFilled, selectionOf, slotAt, unfilledRequiredSlots, CONSERVATIVE_TONE, } from './composer.js';
export { advanced, nextPhase, timestampFor, PHASES } from './clock.js';
export { drawWithoutReplacement, floatAt, fnv1a32, intAt, pickAt, seedFrom } from './rng.js';
export { identityNodeId, puppetIdentity, STATE_VERSION } from './state.js';
//# sourceMappingURL=index.js.map